import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Plus, 
  UserPlus, 
  Ticket, 
  Calendar, 
  FileText,
  Printer,
  Package,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';

const quickActions = [
  { 
    label: 'Nuovo Cliente', 
    icon: UserPlus, 
    path: '/clienti/nuovo',
    color: 'text-cyan-500',
    description: 'Crea anagrafica cliente'
  },
  { 
    label: 'Nuovo Ticket', 
    icon: Ticket, 
    action: 'new-ticket',
    navigateTo: '/tickets',
    color: 'text-orange-500',
    description: 'Apri richiesta assistenza'
  },
  { 
    label: 'Nuovo Appuntamento', 
    icon: Calendar, 
    action: 'new-appointment',
    navigateTo: '/calendario',
    color: 'text-green-500',
    description: 'Pianifica intervento'
  },
  { 
    label: 'Nuovo Documento', 
    icon: FileText, 
    action: 'new-document',
    navigateTo: '/documenti',
    color: 'text-purple-500',
    description: 'Crea preventivo/ordine'
  },
  { 
    label: 'Nuovo Impianto', 
    icon: Printer, 
    action: 'new-impianto',
    navigateTo: '/impianti',
    color: 'text-blue-500',
    description: 'Registra nuovo impianto'
  },
];

export default function QuickActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const handleAction = (action) => {
    setIsOpen(false);
    
    if (action.path) {
      navigate(action.path);
      return;
    }
    
    if (action.action) {
      // Dispatch custom event for the target page to handle
      window.dispatchEvent(new CustomEvent(`open-${action.action}`));
      
      // Navigate to the page if not already there
      if (action.navigateTo && location.pathname !== action.navigateTo) {
        navigate(action.navigateTo);
      }
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "rounded-md h-9 w-9 transition-all duration-300",
            "bg-gradient-to-br from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white",
            isOpen && "rotate-45"
          )}
          title="Azioni rapide: nuovo cliente, ticket, appuntamento, documento, impianto"
          data-testid="fab-button"
        >
          {isOpen ? (
            <X className="h-4 w-4 text-white" />
          ) : (
            <Plus className="h-4 w-4 text-white" />
          )}
        </Button>
      </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          align="end" 
          side="bottom" 
          className="w-64 mt-2 rounded-lg shadow-xl border-border/50"
          data-testid="quick-actions-menu"
        >
          <div className="px-3 py-2 border-b border-border/50">
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
              Azioni Rapide
            </p>
          </div>
          
          {quickActions.map((action, index) => (
            <DropdownMenuItem
              key={action.label}
              onClick={() => handleAction(action)}
              className="cursor-pointer py-3 px-3 focus:bg-accent"
              data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-3 w-full">
                <div className={cn(
                  "h-9 w-9 rounded-lg flex items-center justify-center",
                  "bg-muted/50"
                )}>
                  <action.icon className={cn("h-5 w-5", action.color)} />
                </div>
                <div className="flex flex-col">
                  <span className="font-medium text-sm">{action.label}</span>
                  <span className="text-xs text-muted-foreground">{action.description}</span>
                </div>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
  );
}
